/**
 * Position Thesis Form Component
 *
 * A form component for managing investment thesis on a position.
 * Includes stop loss, take profit, conviction level, duration, and structured thesis input.
 *
 * Multi-version support: Each position can have multiple thesis versions.
 * Users can view historical versions and create new versions.
 *
 * Structured Thesis: Three-section input with:
 * - Statement: Core thesis (max 1000 chars)
 * - Pillars: Supporting arguments (max 10 items)
 * - Risks: Key risks (max 10 items)
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { Picker } from '@react-native-picker/picker';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigation } from '@react-navigation/native';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { DatePicker } from '../ui/DatePicker';
import { LongTextField } from '../ui/LongTextField';
import { useTheme } from '../../contexts/ThemeContext';
import { Ionicons } from '@expo/vector-icons';
import { showSuccess, showError, showConfirm } from '../../utils/alert';
import * as positionThesisApi from '../../services/api/positionThesisApi';
import { createSession } from '../../services/chatApi';
import type { ChatSession } from '../../services/chatApi';
import type { ThesisCommand } from '../../services/chatStream';
import { PositionThesis as PositionThesisType } from '../../services/api/positionThesisApi';
import { useTranslation } from 'react-i18next';
import {
  useThemeTags,
  useThemeRegistry,
  registryToMap,
} from '../../services/api/queries/useThemeTags';
import { translateTheme } from '../../utils/translationHelpers';
import { ThesisChangelog } from './ThesisChangelog';
import {
  StructuredThesisFormData,
  parseThesisText,
  EMPTY_STRUCTURED_THESIS,
  THESIS_LIST_ITEM_MAX_LENGTH,
} from '../../types/thesis';

// Re-export for convenience
export type PositionThesis = PositionThesisType;

export interface PositionThesisFormData {
  stopLossPrice: string;
  takeProfitPrice: string;
  convictionLevel: number | null;
  duration: 'Core' | 'Growth' | 'Speculative' | null;
  thesis: StructuredThesisFormData;
  remindAt: string | null;
}

export interface PositionThesisFormProps {
  positionId: number;
  currency?: string;
  symbol?: string;
  companyName?: string;
  onSuccess?: () => void;
  onNavigateToChat?: (params: {
    sessionId?: string;
    initialMessage?: string;
    autoSend?: boolean;
    initialCommand?: ThesisCommand;
  }) => void;
}

// Strategy tab configuration
interface StrategyOption {
  value: 'Core' | 'Growth' | 'Speculative';
  labelKey: string;
  defaultLabel: string;
  subtitleKey: string;
  defaultSubtitle: string;
  icon: keyof typeof Ionicons.glyphMap;
}

const STRATEGY_OPTIONS: StrategyOption[] = [
  {
    value: 'Core',
    labelKey: 'portfolio.durationCoreHolding',
    defaultLabel: 'Core',
    subtitleKey: 'portfolio.durationCoreHoldingDesc',
    defaultSubtitle: '3+ Years',
    icon: 'shield-outline',
  },
  {
    value: 'Growth',
    labelKey: 'portfolio.durationMidTerm',
    defaultLabel: 'Growth',
    subtitleKey: 'portfolio.durationMidTermDesc',
    defaultSubtitle: '1-2 Years',
    icon: 'trending-up',
  },
  {
    value: 'Speculative',
    labelKey: 'portfolio.durationShortTerm',
    defaultLabel: 'Speculative',
    subtitleKey: 'portfolio.durationShortTermDesc',
    defaultSubtitle: 'Weeks/Months',
    icon: 'flash',
  },
];

export function PositionThesisForm({
  positionId,
  currency = 'USD',
  symbol,
  companyName,
  onSuccess,
  onNavigateToChat,
}: PositionThesisFormProps) {
  const { theme } = useTheme();
  const { t, i18n } = useTranslation();
  const themesQuery = useThemeTags(symbol);
  const registryQuery = useThemeRegistry();
  const themeRegistryMap = registryToMap(registryQuery.data);
  const styles = useMemo(() => createThemedStyles(theme), [theme]);
  const navigation = useNavigation<any>();

  // Version state - only track selected index, use theses directly from query
  const [selectedVersionIndex, setSelectedVersionIndex] = useState(0); // 0 = latest

  // Form state
  const [formData, setFormData] = useState<PositionThesisFormData>({
    stopLossPrice: '',
    takeProfitPrice: '',
    convictionLevel: null,
    duration: null,
    thesis: {
      statement: '',
      pillars: [],
      risks: [],
    },
    remindAt: null,
  });
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveFeedback, setSaveFeedback] = useState<{
    kind: 'success' | 'error';
    message: string;
  } | null>(null);
  const [building, setBuilding] = useState(false);
  const queryClient = useQueryClient();

  // Track the last loaded thesis ID to prevent unnecessary updates
  const lastLoadedThesisId = useRef<number | null>(null);

  // Helper functions for managing pillars
  const addPillar = () => {
    if (formData.thesis.pillars.length >= 10) return;
    setFormData({
      ...formData,
      thesis: {
        ...formData.thesis,
        pillars: [...formData.thesis.pillars, ''],
      },
    });
    setIsDirty(true);
  };

  const updatePillar = (index: number, value: string) => {
    const newPillars = [...formData.thesis.pillars];
    newPillars[index] = value;
    setFormData({
      ...formData,
      thesis: {
        ...formData.thesis,
        pillars: newPillars,
      },
    });
    setIsDirty(true);
  };

  const removePillar = (index: number) => {
    setFormData({
      ...formData,
      thesis: {
        ...formData.thesis,
        pillars: formData.thesis.pillars.filter((_, i) => i !== index),
      },
    });
    setIsDirty(true);
  };

  // Helper functions for managing risks
  const addRisk = () => {
    if (formData.thesis.risks.length >= 10) return;
    setFormData({
      ...formData,
      thesis: {
        ...formData.thesis,
        risks: [...formData.thesis.risks, ''],
      },
    });
    setIsDirty(true);
  };

  const updateRisk = (index: number, value: string) => {
    const newRisks = [...formData.thesis.risks];
    newRisks[index] = value;
    setFormData({
      ...formData,
      thesis: {
        ...formData.thesis,
        risks: newRisks,
      },
    });
    setIsDirty(true);
  };

  const removeRisk = (index: number) => {
    setFormData({
      ...formData,
      thesis: {
        ...formData.thesis,
        risks: formData.thesis.risks.filter((_, i) => i !== index),
      },
    });
    setIsDirty(true);
  };

  // Fetch all thesis versions - returns array sorted by updated_at DESC
  const { data: theses = [], isLoading } = useQuery({
    queryKey: ['positionThesis', positionId],
    queryFn: () => positionThesisApi.getPositionTheses(positionId),
    enabled: !!positionId,
  });

  // Reset selected index if theses array changes and index is out of bounds
  useEffect(() => {
    if (selectedVersionIndex >= theses.length) {
      setSelectedVersionIndex(0);
    }
  }, [theses.length, selectedVersionIndex]);

  // Get currently selected thesis ID - use simple value to prevent infinite loop
  const currentThesisId = useMemo(() => {
    return theses.length > 0 && selectedVersionIndex >= 0
      ? (theses[selectedVersionIndex]?.id ?? null)
      : null;
  }, [theses, selectedVersionIndex]);

  // Get the current thesis object (memoized separately)
  const currentThesis = useMemo(() => {
    if (currentThesisId === null) return null;
    return theses.find((v) => v.id === currentThesisId) || null;
  }, [theses, currentThesisId]);

  // ── Build with TopNote handler (spec 3.2 entry A) ──
  // Creates a fresh, titled chat session and seeds it with a structured
  // build_thesis command: symbol, this position as target, and — when a thesis
  // already exists — the latest saved version as the seed snapshot.
  // (Declared below the theses query on purpose: the seed reads it.)
  const handleBuildWithTopNote = useCallback(async () => {
    if (!symbol) return;
    try {
      setBuilding(true);
      const session = await createSession(`📐 ${symbol} thesis`);
      // Seed the sessions cache BEFORE navigating. This is a raw createSession
      // (not the chat shell's mutation), so without this the
      // ['chat-sessions','active'] cache never learns the session exists and
      // useChatSessions' vanish-check "self-heals" the selection onto an old
      // session on arrival — dropping the build_thesis command (blank session
      // until a manual refresh; bug A, 2026-10-04). Invalidate behind the seed
      // so the list also refetches with the created row.
      queryClient.setQueryData<ChatSession[]>(['chat-sessions', 'active'], (old) =>
        old ? [session, ...old] : [session]
      );
      void queryClient.invalidateQueries({ queryKey: ['chat-sessions', 'active'] });
      const latest = theses.length > 0 ? theses[0] : null;
      const parsed = latest ? parseThesisText(latest.thesis_text) : null;
      const seed =
        latest && parsed
          ? {
              statement: parsed.statement,
              pillars: parsed.pillars,
              risks: parsed.risks,
              conviction_level: latest.conviction_level,
              duration: latest.duration,
              entry_price: latest.entry_price != null ? Number(latest.entry_price) : null,
              // Must ride along: thesis-save writes these unconditionally, so
              // a seed missing them would ERASE them on the seeded save.
              stop_loss_price:
                latest.stop_loss_price != null ? Number(latest.stop_loss_price) : null,
              take_profit_price:
                latest.take_profit_price != null ? Number(latest.take_profit_price) : null,
              remind_at: latest.remind_at,
            }
          : null;
      const navParams = {
        sessionId: session.id,
        initialCommand: {
          command: 'build_thesis' as const,
          symbol,
          target: { kind: 'position' as const, id: positionId },
          seed_snapshot: seed,
        },
      };
      if (onNavigateToChat) onNavigateToChat(navParams);
      else navigation.navigate('Chat', { screen: 'ChatScreen', params: navParams });
    } catch (e) {
      showError((e as Error).message);
    } finally {
      setBuilding(false);
    }
  }, [symbol, theses, positionId, navigation, onNavigateToChat, queryClient]);

  // The theme this thesis was rated against, frozen at save time (spec 006).
  const frozenThemeSlug = currentThesis?.theme ?? null;

  // ...and the theme the mapping names *now*, which is a live fact about the
  // ticker rather than about the thesis. The two are only compared, never merged.
  const livePrimarySlug = useMemo(
    () => (themesQuery.data?.themes ?? []).find((tag) => tag.role === 'primary')?.slug ?? null,
    [themesQuery.data]
  );

  // Helper to format version date for selector (YYYY-MM-dd HH:MM) in local timezone
  const formatVersionDate = (dateString: string): string => {
    // Ensure the date string is treated as UTC if no timezone is specified
    // Backend returns ISO 8601 format like "2026-02-23T09:08:00" which needs 'Z' suffix
    let normalizedDateString = dateString;

    // If no timezone indicator (Z, +, or - offset), append Z to treat as UTC
    const hasTimezone =
      dateString.includes('Z') ||
      dateString.includes('+') ||
      (dateString.includes('T') && dateString.includes('-', dateString.indexOf('T') + 1));

    if (!hasTimezone && dateString.includes('T')) {
      normalizedDateString = dateString + 'Z';
    } else if (!hasTimezone && dateString.includes(' ')) {
      normalizedDateString = dateString.replace(' ', 'T') + 'Z';
    }

    const date = new Date(normalizedDateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  // Helper to convert form data to API format
  const convertFormDataToApiCall = (formData: PositionThesisFormData): any => {
    const result: any = {};

    // Convert structured thesis to API format
    const hasThesisContent =
      formData.thesis.statement?.trim() ||
      formData.thesis.pillars.some((p) => p.trim()) ||
      formData.thesis.risks.some((r) => r.trim());

    if (hasThesisContent) {
      result.structured_thesis = {
        statement: formData.thesis.statement || '',
        pillars: formData.thesis.pillars.filter((p) => p.trim()),
        risks: formData.thesis.risks.filter((r) => r.trim()),
      };
    }

    if (formData.stopLossPrice && formData.stopLossPrice.trim()) {
      result.stop_loss_price = parseFloat(formData.stopLossPrice);
    }
    if (formData.takeProfitPrice && formData.takeProfitPrice.trim()) {
      result.take_profit_price = parseFloat(formData.takeProfitPrice);
    }
    if (formData.convictionLevel !== null && formData.convictionLevel !== undefined) {
      result.conviction_level = formData.convictionLevel;
    }
    if (formData.duration) {
      result.duration = formData.duration;
    }
    // Reminder date (optional)
    result.remind_at = formData.remindAt;

    return result;
  };

  // Helper to format last updated date
  const formatLastUpdated = (dateString: string): string => {
    const date = new Date(dateString);
    return `Last updated: ${date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })}`;
  };

  // Populate form when current thesis changes
  useEffect(() => {
    // Only update if the thesis ID actually changed
    if (currentThesisId !== null && currentThesisId !== lastLoadedThesisId.current) {
      const thesis = theses.find((v) => v.id === currentThesisId);
      if (thesis) {
        // Parse the thesis text to get structured data
        const parsedThesis = parseThesisText(thesis.thesis_text);

        setFormData({
          stopLossPrice: thesis.stop_loss_price?.toString() || '',
          takeProfitPrice: thesis.take_profit_price?.toString() || '',
          convictionLevel: thesis.conviction_level,
          duration: thesis.duration,
          thesis: parsedThesis,
          remindAt: thesis.remind_at ?? null,
        });
        setIsDirty(false);
        lastLoadedThesisId.current = currentThesisId;
      }
    } else if (currentThesisId === null && lastLoadedThesisId.current !== null) {
      // Reset form if no thesis
      setFormData({
        stopLossPrice: '',
        takeProfitPrice: '',
        convictionLevel: null,
        duration: null,
        thesis: { ...EMPTY_STRUCTURED_THESIS },
        remindAt: null,
      });
      setIsDirty(false);
      lastLoadedThesisId.current = null;
    }
  }, [currentThesisId, theses]);

  // Create mutation (always creates new version)
  const createMutation = useMutation({
    mutationFn: (data: PositionThesisFormData) => {
      // Build structured thesis object
      const hasThesisContent =
        data.thesis.statement?.trim() ||
        data.thesis.pillars.some((p) => p.trim()) ||
        data.thesis.risks.some((r) => r.trim());

      const structuredThesis = hasThesisContent
        ? {
            statement: data.thesis.statement || '',
            pillars: data.thesis.pillars.filter((p) => p.trim()),
            risks: data.thesis.risks.filter((r) => r.trim()),
          }
        : null;

      return positionThesisApi.createPositionThesis(
        positionId,
        '', // Empty thesis_text when using structured_thesis
        data.stopLossPrice ? parseFloat(data.stopLossPrice) : null,
        data.takeProfitPrice ? parseFloat(data.takeProfitPrice) : null,
        data.convictionLevel,
        data.duration,
        structuredThesis,
        undefined, // entryPrice (not used by position form)
        data.remindAt
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['positionThesis', positionId] });
      showSuccess('Thesis saved successfully');
      setSaveFeedback({
        kind: 'success',
        message: t('portfolio.thesisSaved', { defaultValue: 'Thesis saved successfully' }),
      });
      setIsDirty(false);
      onSuccess?.();
    },
    onError: (error: Error) => {
      showError(error.message);
      setSaveFeedback({ kind: 'error', message: error.message });
    },
  });

  // Update mutation - creates new version based on current selection
  const updateMutation = useMutation({
    mutationFn: (data: PositionThesisFormData) => {
      // Use PUT endpoint which creates a new version
      return positionThesisApi.updatePositionThesis(
        currentThesis!.id,
        convertFormDataToApiCall(data)
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['positionThesis', positionId] });
      showSuccess('New version saved successfully');
      setSaveFeedback({
        kind: 'success',
        message: t('portfolio.thesisSaved', { defaultValue: 'New version saved successfully' }),
      });
      setIsDirty(false);
      onSuccess?.();
    },
    onError: (error: Error) => {
      showError(error.message);
      setSaveFeedback({ kind: 'error', message: error.message });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (thesisId: number) => positionThesisApi.deletePositionThesis(thesisId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['positionThesis', positionId] });
      showSuccess('Version deleted successfully');
      // Reset to latest version after deletion
      setSelectedVersionIndex(0);
      onSuccess?.();
    },
    onError: (error: Error) => {
      showError(error.message);
    },
  });

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // For new theses (no versions exist), at least one field must have a non-empty value
    if (theses.length === 0) {
      const hasThesisStatement =
        formData.thesis.statement && formData.thesis.statement.trim().length > 0;
      const hasThesisPillars = formData.thesis.pillars.some((p) => p.trim());
      const hasThesisRisks = formData.thesis.risks.some((r) => r.trim());
      const hasThesisContent = hasThesisStatement || hasThesisPillars || hasThesisRisks;
      const hasOtherValue =
        (formData.stopLossPrice && formData.stopLossPrice.trim() !== '') ||
        (formData.takeProfitPrice && formData.takeProfitPrice.trim() !== '') ||
        formData.convictionLevel !== null ||
        formData.duration !== null;

      if (!hasThesisContent && !hasOtherValue) {
        newErrors.thesisStatement = t('portfolio.atLeastOneFieldRequired', {
          defaultValue:
            'At least one field (thesis statement, pillars, risks, stop loss, take profit, conviction, or duration) is required',
        });
      }
    }

    // Validate stop loss price
    if (formData.stopLossPrice && formData.stopLossPrice.trim()) {
      const num = parseFloat(formData.stopLossPrice);
      if (isNaN(num) || num <= 0) {
        newErrors.stopLossPrice = t('portfolio.mustBeValidPositiveNumber', {
          defaultValue: 'Must be a valid positive number',
        });
      }
    }

    // Validate take profit price
    if (formData.takeProfitPrice && formData.takeProfitPrice.trim()) {
      const num = parseFloat(formData.takeProfitPrice);
      if (isNaN(num) || num <= 0) {
        newErrors.takeProfitPrice = t('portfolio.mustBeValidPositiveNumber', {
          defaultValue: 'Must be a valid positive number',
        });
      }
    }

    // Validate thesis statement length
    if (formData.thesis.statement && formData.thesis.statement.length > 1000) {
      newErrors.thesisStatement = t('portfolio.thesisStatementMaxLength', {
        defaultValue: 'Thesis statement must be 1000 characters or less',
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    try {
      // Always create a new version (either from scratch or based on current selection)
      if (theses.length === 0 || !currentThesis) {
        await createMutation.mutateAsync(formData);
      } else {
        await updateMutation.mutateAsync(formData);
      }
    } catch {
      // The mutation's onError already surfaced the reason (toast +
      // inline banner); swallow so the rejection stays handled.
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!currentThesis) return;

    const confirmed = await showConfirm(
      t('portfolio.deleteThisVersion', { defaultValue: 'Delete This Version' }),
      t('portfolio.deleteThesisConfirmation', {
        defaultValue:
          'Are you sure you want to delete the thesis from {{date}}? This action cannot be undone.',
        date: formatVersionDate(currentThesis.updated_at),
      }),
      {
        confirmText: t('common.delete', { defaultValue: 'Delete' }),
        cancelText: t('common.cancel', { defaultValue: 'Cancel' }),
        destructiveAction: true,
      }
    );

    if (confirmed) {
      setIsSaving(true);
      try {
        await deleteMutation.mutateAsync(currentThesis.id);
        // After deletion, form will reset to latest or empty
      } catch {
        // onError already surfaced the failure reason.
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleCancel = () => {
    // Revert to current thesis data
    if (currentThesis) {
      const parsedThesis = parseThesisText(currentThesis.thesis_text);
      setFormData({
        stopLossPrice: currentThesis.stop_loss_price?.toString() || '',
        takeProfitPrice: currentThesis.take_profit_price?.toString() || '',
        convictionLevel: currentThesis.conviction_level,
        duration: currentThesis.duration,
        thesis: parsedThesis,
        remindAt: currentThesis.remind_at ?? null,
      });
      setIsDirty(false);
    }
  };

  const handleVersionChange = (index: number) => {
    setSelectedVersionIndex(index);
    const version = theses[index];
    const parsedThesis = parseThesisText(version.thesis_text);
    setFormData({
      stopLossPrice: version.stop_loss_price?.toString() || '',
      takeProfitPrice: version.take_profit_price?.toString() || '',
      convictionLevel: version.conviction_level,
      duration: version.duration,
      thesis: parsedThesis,
      remindAt: version.remind_at ?? null,
    });
    setIsDirty(false);
  };

  const hasContent = (): boolean => {
    // Check if any field has content
    const hasThesisStatement = formData.thesis.statement && formData.thesis.statement.trim() !== '';
    const hasThesisPillars = formData.thesis.pillars.some((p) => p.trim());
    const hasThesisRisks = formData.thesis.risks.some((r) => r.trim());

    if (theses.length === 0) {
      // For new theses, require at least one field with actual value
      // (theme counts as content too 鈥?backend accepts theme-only creates)
      return (
        hasThesisStatement ||
        hasThesisPillars ||
        hasThesisRisks ||
        (formData.stopLossPrice && formData.stopLossPrice.trim() !== '') ||
        (formData.takeProfitPrice && formData.takeProfitPrice.trim() !== '') ||
        formData.convictionLevel !== null ||
        formData.duration !== null
      );
    }
    // For existing theses, allow any field to be updated
    return (
      hasThesisStatement ||
      hasThesisPillars ||
      hasThesisRisks ||
      formData.stopLossPrice.trim() !== '' ||
      formData.takeProfitPrice.trim() !== '' ||
      formData.convictionLevel !== null ||
      formData.duration !== null
    );
  };

  // Check if user has unsaved changes
  const hasUnsavedChanges = (): boolean => {
    if (!currentThesis) return false;

    const parsedThesis = parseThesisText(currentThesis.thesis_text);

    const currentValues = {
      stopLossPrice: formData.stopLossPrice,
      takeProfitPrice: formData.takeProfitPrice,
      convictionLevel: formData.convictionLevel,
      duration: formData.duration,
      thesisStatement: formData.thesis.statement,
      thesisPillars: formData.thesis.pillars,
      thesisRisks: formData.thesis.risks,
      remindAt: formData.remindAt,
    };

    const thesisValues = {
      stopLossPrice: currentThesis.stop_loss_price?.toString() || '',
      takeProfitPrice: currentThesis.take_profit_price?.toString() || '',
      convictionLevel: currentThesis.conviction_level,
      duration: currentThesis.duration,
      thesisStatement: parsedThesis.statement,
      thesisPillars: parsedThesis.pillars,
      thesisRisks: parsedThesis.risks,
      remindAt: currentThesis.remind_at ?? null,
    };

    return JSON.stringify(currentValues) !== JSON.stringify(thesisValues);
  };

  useEffect(() => {
    if (saveFeedback?.kind !== 'success') return;
    const id = setTimeout(() => setSaveFeedback(null), 3000);
    return () => clearTimeout(id);
  }, [saveFeedback]);

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={[styles.loadingText, { color: theme.colors.textSecondary }]}>
          {t('portfolio.loadingThesis', { defaultValue: 'Loading thesis...' })}
        </Text>
      </View>
    );
  }

  return (
    <>
      {saveFeedback && (
        <View
          testID="thesis-save-feedback"
          style={[
            styles.saveFeedbackBanner,
            {
              backgroundColor:
                saveFeedback.kind === 'success' ? theme.colors.success : theme.colors.error,
            },
          ]}
        >
          <Text style={[styles.saveFeedbackText, { color: theme.colors.textInverse }]}>
            {saveFeedback.message}
          </Text>
        </View>
      )}
      <ScrollView style={styles.outerContainer} contentContainerStyle={{ paddingBottom: 300 }}>
        <Card style={styles.positionCard}>
          {/* Title Row with AI Buttons + Version Selector */}
          <View style={styles.titleRow}>
            <Text style={[styles.title, { color: theme.colors.text }]}>
              {t('portfolio.positionThesis', { defaultValue: 'Position Thesis' })}
            </Text>

            {/* AI action buttons 鈥?right-aligned */}
            {symbol && (
              <View style={styles.aiButtonsRow}>
                <TouchableOpacity
                  style={[styles.aiButton, { borderColor: theme.colors.primary }]}
                  onPress={handleBuildWithTopNote}
                  disabled={isSaving || building}
                  activeOpacity={0.7}
                >
                  {building ? (
                    <ActivityIndicator size="small" color={theme.colors.primary} />
                  ) : (
                    <Ionicons name="chatbubble-outline" size={14} color={theme.colors.primary} />
                  )}
                  <Text style={[styles.aiButtonText, { color: theme.colors.primary }]}>
                    {t('portfolio.buildWithTopNote', { defaultValue: 'Build with TopNote' })}
                  </Text>
                </TouchableOpacity>
              </View>
            )}
          </View>

          {/* Version Selector - Only show if multiple versions exist */}
          {theses.length > 1 && (
            <View
              style={[
                styles.versionPickerWrapper,
                {
                  borderColor: theme.colors.border,
                  borderWidth: theme.id === 'bauhaus' ? 2 : 1,
                  backgroundColor: theme.colors.backgroundElevated,
                },
              ]}
            >
              <Picker
                selectedValue={selectedVersionIndex}
                onValueChange={(itemValue: number) => handleVersionChange(itemValue)}
                enabled={!isSaving}
                style={[
                  styles.versionPicker,
                  {
                    color: theme.colors.text,
                  },
                ]}
                dropdownIconColor={theme.colors.textSecondary}
                mode="dropdown"
              >
                {theses.map((thesis, index) => (
                  <Picker.Item
                    key={thesis.id}
                    label={
                      index === 0
                        ? t('portfolio.latest', { defaultValue: 'Latest' })
                        : formatVersionDate(thesis.updated_at)
                    }
                    value={index}
                    color={theme.colors.text}
                  />
                ))}
              </Picker>
              {/* Web-only dark mode styles for select options */}
              {Platform.OS === 'web' && (
                <style>{`
                    select option {
                      background-color: ${theme.colors.backgroundElevated};
                      color: ${theme.colors.text};
                    }
                  `}</style>
              )}
            </View>
          )}

          {/* Version Info - Show when viewing historical version */}
          {selectedVersionIndex > 0 && currentThesis && (
            <View
              style={[styles.versionBanner, { backgroundColor: theme.colors.warningBackground }]}
            >
              <Text style={[styles.versionBannerText, { color: theme.colors.warningText }]}>
                {t('portfolio.viewingHistoricalVersion', {
                  defaultValue: 'Viewing historical version from {{date}}',
                  date: formatVersionDate(currentThesis.updated_at),
                })}
              </Text>
              <Text style={[styles.versionBannerSubtext, { color: theme.colors.warningText }]}>
                {t('portfolio.savingWillCreateNewVersion', {
                  defaultValue: 'Saving will create a new version',
                })}
              </Text>
            </View>
          )}

          {/* Conviction Strategy Tabs */}
          <View style={styles.fieldGroup}>
            <Text style={[styles.label, { color: theme.colors.textSecondary }]}>
              {t('portfolio.convictionStrategy', { defaultValue: 'Conviction Strategy' })}
            </Text>
            <View style={styles.strategyTabsContainer}>
              {STRATEGY_OPTIONS.map((option) => {
                const isSelected = formData.duration === option.value;
                return (
                  <TouchableOpacity
                    key={option.value}
                    style={[
                      styles.strategyTab,
                      {
                        borderColor: isSelected ? theme.colors.primary : theme.colors.border,
                        backgroundColor: isSelected
                          ? theme.colors.primary + '18'
                          : theme.colors.backgroundElevated,
                        borderWidth: isSelected ? 2 : theme.id === 'bauhaus' ? 2 : 1,
                      },
                    ]}
                    onPress={() => {
                      setFormData({ ...formData, duration: isSelected ? null : option.value });
                      setIsDirty(true);
                    }}
                    disabled={isSaving}
                    activeOpacity={0.7}
                  >
                    <Ionicons
                      name={option.icon}
                      size={20}
                      color={isSelected ? theme.colors.primary : theme.colors.textTertiary}
                      style={styles.strategyTabIcon}
                    />
                    <Text
                      style={[
                        styles.strategyTabLabel,
                        { color: isSelected ? theme.colors.primary : theme.colors.text },
                      ]}
                    >
                      {t(option.labelKey, { defaultValue: option.defaultLabel })}
                    </Text>
                    <Text
                      style={[
                        styles.strategyTabSubtitle,
                        { color: isSelected ? theme.colors.primary : theme.colors.textTertiary },
                      ]}
                    >
                      {t(option.subtitleKey, { defaultValue: option.defaultSubtitle })}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>

          {/* Research themes (spec 006 D4): read-only pills from the wiki registry */}
          {symbol && (
            <View style={styles.fieldGroup}>
              <Text style={[styles.label, { color: theme.colors.textSecondary }]}>
                {t('portfolio.stockThemesLabel', { defaultValue: 'Research themes' })}
              </Text>
              {themesQuery.isLoading ? (
                <ActivityIndicator size="small" color={theme.colors.primary} />
              ) : themesQuery.isError ? (
                <Text style={[styles.helperText, { color: theme.colors.textTertiary }]}>
                  {t('portfolio.stockThemesUnavailable', {
                    defaultValue: 'Theme registry unavailable',
                  })}
                </Text>
              ) : (themesQuery.data?.themes ?? []).length === 0 ? (
                <Text style={[styles.helperText, { color: theme.colors.textTertiary }]}>
                  {t('portfolio.stockThemesEmpty', { defaultValue: 'Not mapped to any theme yet' })}
                </Text>
              ) : (
                <View style={styles.themePillWrap}>
                  {(themesQuery.data?.themes ?? []).map((tag) => (
                    <View
                      key={`${tag.theme_id}`}
                      style={[
                        styles.themePill,
                        {
                          backgroundColor: theme.colors.backgroundElevated,
                          borderColor:
                            tag.role === 'primary' ? theme.colors.primary : theme.colors.border,
                        },
                      ]}
                    >
                      <Text style={[styles.themePillText, { color: theme.colors.text }]}>
                        {translateTheme(tag.slug, themeRegistryMap, i18n.language)}
                      </Text>
                      {tag.role === 'primary' && (
                        <Text
                          testID="theme-primary-badge"
                          style={[styles.themePillBadge, { color: theme.colors.primary }]}
                        >
                          {t('portfolio.themePrimaryBadge', { defaultValue: 'Primary' })}
                        </Text>
                      )}
                    </View>
                  ))}
                </View>
              )}
              {/* The frozen slug and the live primary are different facts and are
                  shown as such: this thesis was *rated against* the theme recorded
                  at rating time, which does not move when the mapping does. Only
                  when the two disagree is the current primary named, so the reader
                  is told about a change rather than a second, competing label. */}
              {frozenThemeSlug && (
                <View testID="thesis-rated-against">
                  <Text style={[styles.helperText, { color: theme.colors.textTertiary }]}>
                    {t('portfolio.thesisRatedAgainst', { defaultValue: 'Rated against' })}:{' '}
                    <Text style={{ color: theme.colors.text }}>
                      {translateTheme(frozenThemeSlug, themeRegistryMap, i18n.language)}
                    </Text>
                  </Text>
                  {livePrimarySlug && livePrimarySlug !== frozenThemeSlug && (
                    <Text style={[styles.helperText, { color: theme.colors.textTertiary }]}>
                      {t('portfolio.thesisCurrentPrimary', {
                        defaultValue: 'Current primary theme',
                      })}
                      :{' '}
                      <Text style={{ color: theme.colors.text }}>
                        {translateTheme(livePrimarySlug, themeRegistryMap, i18n.language)}
                      </Text>
                    </Text>
                  )}
                  <Text style={[styles.helperText, { color: theme.colors.textTertiary }]}>
                    {t('portfolio.thesisRatedAgainstHelp', {
                      defaultValue:
                        'The theme this thesis was rated against, as recorded. It does not change when the primary theme does.',
                    })}
                  </Text>
                </View>
              )}
              <Text style={[styles.helperText, { color: theme.colors.textTertiary }]}>
                {t('portfolio.stockThemesHelp', {
                  defaultValue: 'Themes come from the research registry and cannot be edited here.',
                })}
              </Text>
            </View>
          )}

          {/* Stop Loss and Take Profit Prices - Side by Side */}
          <View style={styles.rowFieldGroup}>
            {/* Stop Loss Price */}
            <View style={styles.halfField}>
              <Text style={[styles.label, { color: theme.colors.textSecondary }]}>
                {t('portfolio.stopLossPrice', { defaultValue: 'Stop Loss Price' })}
              </Text>
              <View style={styles.inputWithCurrencyWrapper}>
                <TextInput
                  testID="stop-loss-input"
                  style={[
                    styles.inputWithCurrency,
                    {
                      backgroundColor: theme.colors.backgroundElevated,
                      borderColor: errors.stopLossPrice ? theme.colors.error : theme.colors.border,
                      color: theme.colors.text,
                    },
                  ]}
                  placeholder="0.00"
                  placeholderTextColor={theme.colors.textTertiary}
                  value={formData.stopLossPrice}
                  onChangeText={(text) => {
                    setFormData({ ...formData, stopLossPrice: text });
                    setErrors({ ...errors, stopLossPrice: undefined });
                    setIsDirty(true);
                  }}
                  keyboardType="decimal-pad"
                  editable={!isSaving}
                />
                <Text style={[styles.currencyInlineLabel, { color: theme.colors.textTertiary }]}>
                  {currency}
                </Text>
              </View>
              {errors.stopLossPrice && (
                <Text style={[styles.errorText, { color: theme.colors.error }]}>
                  {errors.stopLossPrice}
                </Text>
              )}
            </View>

            {/* Take Profit Price */}
            <View style={styles.halfField}>
              <Text style={[styles.label, { color: theme.colors.textSecondary }]}>
                {t('portfolio.takeProfitPrice', { defaultValue: 'Take Profit Price' })}
              </Text>
              <View style={styles.inputWithCurrencyWrapper}>
                <TextInput
                  testID="take-profit-input"
                  style={[
                    styles.inputWithCurrency,
                    {
                      backgroundColor: theme.colors.backgroundElevated,
                      borderColor: errors.takeProfitPrice
                        ? theme.colors.error
                        : theme.colors.border,
                      color: theme.colors.text,
                    },
                  ]}
                  placeholder="0.00"
                  placeholderTextColor={theme.colors.textTertiary}
                  value={formData.takeProfitPrice}
                  onChangeText={(text) => {
                    setFormData({ ...formData, takeProfitPrice: text });
                    setErrors({ ...errors, takeProfitPrice: undefined });
                    setIsDirty(true);
                  }}
                  keyboardType="decimal-pad"
                  editable={!isSaving}
                />
                <Text style={[styles.currencyInlineLabel, { color: theme.colors.textTertiary }]}>
                  {currency}
                </Text>
              </View>
              {errors.takeProfitPrice && (
                <Text style={[styles.errorText, { color: theme.colors.error }]}>
                  {errors.takeProfitPrice}
                </Text>
              )}
            </View>
          </View>

          {/* Reminder Date */}
          <View style={styles.fieldGroup}>
            <Text style={[styles.label, { color: theme.colors.textSecondary }]}>
              {t('portfolio.reminderDate', { defaultValue: 'Reminder date' })}
            </Text>
            <DatePicker
              testID="reminder-date-picker"
              value={formData.remindAt}
              onChange={(date) => {
                setFormData({ ...formData, remindAt: date });
                setIsDirty(true);
              }}
              placeholder="YYYY-MM-DD"
            />
            <Text style={[styles.helperText, { color: theme.colors.textTertiary }]}>
              {t('portfolio.reminderDateHelp', {
                defaultValue: 'Optional: Set a date to remind yourself to review this thesis',
              })}
            </Text>
          </View>

          {/* Structured Thesis Sections */}
          <View style={styles.fieldGroup}>
            {/* Thesis Statement Section */}
            <View style={styles.thesisSection}>
              <Text style={[styles.sectionHeader, { color: theme.colors.text }]}>
                {t('portfolio.thesisStatement', { defaultValue: 'Thesis Statement' })}
              </Text>
              <Text style={[styles.label, { color: theme.colors.textSecondary }]}>
                {t('portfolio.thesisStatementHint', {
                  defaultValue: 'Summarize your investment thesis in one paragraph.',
                })}
              </Text>
              <TextInput
                testID="thesis-statement-input"
                style={[
                  styles.textArea,
                  {
                    backgroundColor: theme.colors.backgroundElevated,
                    borderColor: errors.thesisStatement ? theme.colors.error : theme.colors.border,
                    color: theme.colors.text,
                  },
                ]}
                placeholder={t('portfolio.thesisStatementPlaceholder', {
                  defaultValue:
                    "e.g., 'NVDA is a strong buy due to their AI dominance, growing data center revenue, and expanding software ecosystem.'",
                })}
                placeholderTextColor={theme.colors.textTertiary}
                value={formData.thesis.statement}
                onChangeText={(text) => {
                  setFormData({
                    ...formData,
                    thesis: { ...formData.thesis, statement: text },
                  });
                  setErrors({ ...errors, thesisStatement: undefined });
                  setIsDirty(true);
                }}
                multiline
                numberOfLines={4}
                maxLength={1000}
                editable={!isSaving}
              />
              <View style={styles.characterCount}>
                <Text style={[styles.characterCountText, { color: theme.colors.textTertiary }]}>
                  {formData.thesis.statement.length} / 1000
                </Text>
                {errors.thesisStatement && (
                  <Text style={[styles.errorText, { color: theme.colors.error }]}>
                    {errors.thesisStatement}
                  </Text>
                )}
              </View>
            </View>

            {/* Key Pillars Section */}
            <View style={styles.thesisSection}>
              <View style={styles.sectionTitleRow}>
                <Text style={[styles.sectionHeader, { color: theme.colors.text }]}>
                  {t('portfolio.keyPillars', { defaultValue: 'Key Pillars' })}
                </Text>
                <Text style={[styles.sectionCount, { color: theme.colors.textSecondary }]}>
                  {formData.thesis.pillars.length} / 10
                </Text>
              </View>
              <Text style={[styles.label, { color: theme.colors.textSecondary }]}>
                {t('portfolio.keyPillarsHint', {
                  defaultValue: 'List the supporting arguments for your thesis.',
                })}
              </Text>

              {formData.thesis.pillars.map((pillar, index) => (
                <View key={`pillar-${index}`} style={styles.listItemRow}>
                  <View style={styles.listItemInputWrapper}>
                    <LongTextField
                      testID={`pillar-input-${index}`}
                      value={pillar}
                      onChangeText={(text) => updatePillar(index, text)}
                      placeholder={t('portfolio.pillarPlaceholder', {
                        defaultValue: `e.g., 'AI market leadership'`,
                      })}
                      modalTitle={t('portfolio.editPillar', { defaultValue: 'Edit Pillar' })}
                      maxLength={THESIS_LIST_ITEM_MAX_LENGTH}
                      editable={!isSaving}
                    />
                  </View>
                  <TouchableOpacity
                    testID={`remove-pillar-${index}`}
                    style={[styles.removeButton, { borderColor: theme.colors.error }]}
                    onPress={() => removePillar(index)}
                    disabled={isSaving}
                  >
                    <Ionicons name="close-circle" size={20} color={theme.colors.error} />
                  </TouchableOpacity>
                </View>
              ))}

              {formData.thesis.pillars.length < 10 && (
                <TouchableOpacity
                  testID="add-pillar-button"
                  style={[styles.addButton, { borderColor: theme.colors.primary }]}
                  onPress={addPillar}
                  disabled={isSaving}
                >
                  <Ionicons name="add-circle" size={20} color={theme.colors.primary} />
                  <Text style={[styles.addButtonText, { color: theme.colors.primary }]}>
                    {t('portfolio.addPillar', { defaultValue: 'Add Pillar' })}
                  </Text>
                </TouchableOpacity>
              )}
            </View>

            {/* Key Risks Section */}
            <View style={styles.thesisSection}>
              <View style={styles.sectionTitleRow}>
                <Text style={[styles.sectionHeader, { color: theme.colors.text }]}>
                  {t('portfolio.keyRisks', { defaultValue: 'Key Risks' })}
                </Text>
                <Text style={[styles.sectionCount, { color: theme.colors.textSecondary }]}>
                  {formData.thesis.risks.length} / 10
                </Text>
              </View>
              <Text style={[styles.label, { color: theme.colors.textSecondary }]}>
                {t('portfolio.keyRisksHint', {
                  defaultValue: 'List the key risks that could invalidate your thesis.',
                })}
              </Text>

              {formData.thesis.risks.map((risk, index) => (
                <View key={`risk-${index}`} style={styles.listItemRow}>
                  <View style={styles.listItemInputWrapper}>
                    <LongTextField
                      testID={`risk-input-${index}`}
                      value={risk}
                      onChangeText={(text) => updateRisk(index, text)}
                      placeholder={t('portfolio.riskPlaceholder', {
                        defaultValue: `e.g., 'China export restrictions'`,
                      })}
                      modalTitle={t('portfolio.editRisk', { defaultValue: 'Edit Risk' })}
                      maxLength={THESIS_LIST_ITEM_MAX_LENGTH}
                      editable={!isSaving}
                    />
                  </View>
                  <TouchableOpacity
                    testID={`remove-risk-${index}`}
                    style={[styles.removeButton, { borderColor: theme.colors.error }]}
                    onPress={() => removeRisk(index)}
                    disabled={isSaving}
                  >
                    <Ionicons name="close-circle" size={20} color={theme.colors.error} />
                  </TouchableOpacity>
                </View>
              ))}

              {formData.thesis.risks.length < 10 && (
                <TouchableOpacity
                  testID="add-risk-button"
                  style={[styles.addButton, { borderColor: theme.colors.primary }]}
                  onPress={addRisk}
                  disabled={isSaving}
                >
                  <Ionicons name="add-circle" size={20} color={theme.colors.primary} />
                  <Text style={[styles.addButtonText, { color: theme.colors.primary }]}>
                    {t('portfolio.addRisk', { defaultValue: 'Add Risk' })}
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          </View>

          {/* Last Updated Date */}
          {currentThesis && currentThesis.updated_at && (
            <Text style={[styles.lastUpdated, { color: theme.colors.textTertiary }]}>
              {formatLastUpdated(currentThesis.updated_at)}
            </Text>
          )}

          {/* Actions */}
          <View style={styles.actions}>
            {currentThesis ? (
              <>
                {/* Delete button - left of save button (when not new or empty) */}
                {!hasUnsavedChanges() && (
                  <TouchableOpacity
                    style={[
                      styles.deleteButton,
                      { borderColor: theme.colors.error, minHeight: 48 },
                    ]}
                    onPress={handleDelete}
                    disabled={isSaving}
                  >
                    <Text style={[styles.deleteButtonText, { color: theme.colors.error }]}>
                      {t('common.delete', { defaultValue: 'Delete' })}
                    </Text>
                  </TouchableOpacity>
                )}

                {/* Cancel button - show when there are unsaved changes */}
                {hasUnsavedChanges() && (
                  <TouchableOpacity
                    style={[
                      styles.cancelButton,
                      { borderColor: theme.colors.border, minHeight: 48 },
                    ]}
                    onPress={handleCancel}
                    disabled={isSaving}
                  >
                    <Text style={[styles.cancelButtonText, { color: theme.colors.textSecondary }]}>
                      {t('common.cancel', { defaultValue: 'Cancel' })}
                    </Text>
                  </TouchableOpacity>
                )}

                <Button
                  title={t('common.save', { defaultValue: 'Save' })}
                  onPress={handleSave}
                  disabled={isSaving || (!isDirty && !hasContent())}
                  loading={isSaving}
                />
              </>
            ) : (
              <Button
                title={t('common.save', { defaultValue: 'Save' })}
                onPress={handleSave}
                disabled={!hasContent() || isSaving}
                loading={isSaving}
              />
            )}
          </View>

          {/* Changelog Section - Show if there are versions */}
          {theses.length > 0 && <ThesisChangelog versions={theses} currency={currency} />}
        </Card>
      </ScrollView>
    </>
  );
}

/**
 * Create themed styles dynamically
 */
function createThemedStyles(theme: any) {
  return StyleSheet.create({
    outerContainer: {
      flex: 1,
    },
    positionCard: {
      margin: 16,
      padding: 16,
    },
    titleRow: {
      marginBottom: theme.spacing.lg,
      flexDirection: 'row' as 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
    },
    aiButtonsRow: {
      flexDirection: 'row' as 'row',
      gap: theme.spacing.sm,
      flexShrink: 0,
    },
    aiButton: {
      flexDirection: 'row' as 'row',
      alignItems: 'center',
      paddingHorizontal: theme.spacing.sm,
      paddingVertical: theme.spacing.xs,
      borderRadius: theme.radii.md,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      gap: 4,
    },
    aiButtonText: {
      fontSize: theme.typography.fontSizeXs,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    title: {
      fontSize: theme.typography.fontSizeXl,
      fontWeight: theme.typography.fontWeightBold,
      marginBottom: theme.spacing.md,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
      letterSpacing: theme.id === 'bauhaus' ? -0.5 : 0,
    },
    versionSelectorWrapper: {
      marginTop: theme.spacing.sm,
    },
    versionPickerWrapper: {
      borderRadius: theme.radii.md,
      marginTop: theme.spacing.sm,
      overflow: 'hidden',
    },
    versionPicker: {
      height: Platform.OS === 'web' ? 44 : undefined,
      fontSize: theme.typography.fontSizeMd,
      backgroundColor: 'transparent',
    },
    versionBanner: {
      padding: theme.spacing.md,
      borderRadius: theme.radii.md,
      marginBottom: theme.spacing.lg,
      borderLeftWidth: 4,
      borderLeftColor: theme.colors.warningBorder,
    },
    versionBannerText: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
      marginBottom: 2,
    },
    versionBannerSubtext: {
      fontSize: theme.typography.fontSizeXs,
      opacity: 0.8,
    },
    label: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
      marginBottom: theme.spacing.xs,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
      letterSpacing: theme.id === 'bauhaus' ? -0.5 : 0,
    },
    helperLabel: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
      marginBottom: theme.spacing.xs,
      color: theme.colors.textSecondary,
    },
    fieldGroup: {
      marginBottom: theme.spacing.lg,
    },
    rowFieldGroup: {
      flexDirection: 'row' as 'row',
      gap: theme.spacing.lg,
      marginBottom: theme.spacing.lg,
    },
    halfField: {
      flex: 1,
      minWidth: 0, // Allow flex item to shrink below content size
    },
    strategyTabsContainer: {
      flexDirection: 'row' as 'row',
      gap: theme.spacing.md,
    },
    strategyTab: {
      flex: 1,
      alignItems: 'center' as 'center',
      justifyContent: 'center' as 'center',
      paddingVertical: theme.spacing.md,
      paddingHorizontal: theme.spacing.sm,
      borderRadius: theme.radii.md,
    },
    strategyTabIcon: {
      marginBottom: theme.spacing.xs,
    },
    strategyTabLabel: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightBold,
      textTransform: theme.id === 'bauhaus' ? ('uppercase' as 'uppercase') : ('none' as 'none'),
      letterSpacing: theme.id === 'bauhaus' ? -0.3 : 0,
    },
    strategyTabSubtitle: {
      fontSize: theme.typography.fontSizeXs,
      marginTop: 2,
    },
    inputWithCurrencyWrapper: {
      flexDirection: 'row' as 'row',
      alignItems: 'center',
      position: 'relative',
    },
    input: {
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderRadius: theme.radii.md,
      padding: theme.spacing.md,
      fontSize: theme.typography.fontSizeMd,
      color: theme.colors.text,
    },
    inputWithCurrency: {
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderRadius: theme.radii.md,
      paddingVertical: theme.spacing.md,
      paddingHorizontal: theme.spacing.sm,
      paddingRight: theme.spacing.md + 35, // Make room for currency label
      fontSize: theme.typography.fontSizeMd,
      color: theme.colors.text,
      minWidth: 0, // Allow flex item to shrink
      flexShrink: 1,
    },
    textArea: {
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderRadius: theme.radii.md,
      padding: theme.spacing.md,
      fontSize: theme.typography.fontSizeMd,
      minHeight: 100,
      textAlignVertical: 'top',
      color: theme.colors.text,
    },
    currencyLabel: {
      position: 'absolute',
      right: theme.spacing.md,
      top: theme.spacing.md + 4,
      fontSize: theme.typography.fontSizeSm,
    },
    currencyInlineLabel: {
      fontSize: theme.typography.fontSizeSm,
      color: theme.colors.textTertiary,
      position: 'absolute',
      right: theme.spacing.md,
    },
    helperText: {
      fontSize: theme.typography.fontSizeSm,
      color: theme.colors.textTertiary,
      marginTop: theme.spacing.xs,
    },
    errorText: {
      fontSize: theme.typography.fontSizeXs,
      color: theme.colors.error,
      marginTop: 2,
    },
    characterCount: {
      alignItems: 'flex-end',
      marginTop: theme.spacing.xs,
    },
    characterCountText: {
      fontSize: theme.typography.fontSizeXs,
      color: theme.colors.textTertiary,
    },
    saveFeedbackBanner: {
      padding: theme.spacing.md,
      borderRadius: theme.radii.md,
      marginBottom: theme.spacing.md,
    },
    saveFeedbackText: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    themePillWrap: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: theme.spacing.sm,
      marginTop: theme.spacing.xs,
    },
    themePill: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.xs,
      paddingVertical: theme.spacing.xs,
      paddingHorizontal: theme.spacing.md,
      borderRadius: theme.radii.lg,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
    },
    themePillText: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    themePillBadge: {
      fontSize: theme.typography.fontSizeXs,
      fontWeight: theme.typography.fontWeightBold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
    lastUpdated: {
      fontSize: theme.typography.fontSizeXs,
      color: theme.colors.textTertiary,
      fontStyle: 'italic',
      marginTop: theme.spacing.sm,
    },
    loadingContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      paddingVertical: 32,
    },
    loadingText: {
      marginTop: theme.spacing.sm,
      fontSize: theme.typography.fontSizeMd,
      color: theme.colors.textSecondary,
    },
    actions: {
      flexDirection: 'row' as 'row',
      gap: theme.spacing.md,
      marginTop: theme.spacing.lg,
    },
    deleteButton: {
      flex: 1,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderRadius: theme.radii.md,
      paddingVertical: 14,
      paddingHorizontal: 20,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 48,
      minWidth: 120, // Match Button width
    },
    deleteButtonText: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
    cancelButton: {
      flex: 1,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderRadius: theme.radii.md,
      paddingVertical: 14,
      paddingHorizontal: 20,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 48,
      minWidth: 120, // Match Button width
    },
    cancelButtonText: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
    // Structured thesis section styles
    thesisSection: {
      marginBottom: theme.spacing.xl,
    },
    sectionTitleRow: {
      flexDirection: 'row' as 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: theme.spacing.xs,
    },
    sectionHeader: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightBold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
      letterSpacing: theme.id === 'bauhaus' ? -0.3 : 0,
    },
    sectionCount: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    addButton: {
      flexDirection: 'row' as 'row',
      alignItems: 'center' as 'center',
      justifyContent: 'center' as 'center',
      paddingVertical: theme.spacing.sm,
      paddingHorizontal: theme.spacing.md,
      borderRadius: theme.radii.md,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      marginTop: theme.spacing.sm,
      gap: theme.spacing.xs,
    },
    addButtonText: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    listItemRow: {
      flexDirection: 'row' as 'row',
      alignItems: 'center' as 'center',
      marginBottom: theme.spacing.sm,
      gap: theme.spacing.sm,
    },
    listItemInputWrapper: {
      flex: 1,
    },
    removeButton: {
      padding: theme.spacing.sm,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderRadius: theme.radii.sm,
      alignItems: 'center' as 'center',
      justifyContent: 'center' as 'center',
      minWidth: 36,
      minHeight: 36,
    },
  });
}
